import { createApi } from '@reduxjs/toolkit/query/react';
import { IResourcesFormInput } from 'pages/private/TrainingManagments/Training/schema';
import { baseQuery } from 'redux/reducers/settings';

interface ApiResponse<T = any> {
  code: number;
  message: string;
  data: T;
}

// Main Evaluation (Trainee Evaluation)
export interface IEvaluationTrainee {
  id: number;
  routine_id: number;
  evaluation_phase: number;
  title: string;
  passing_percentage: number;
  is_active: boolean;
  start_date_time: string;
  end_date_time: string;
  // add other fields if needed
}

export interface IResources {
  name: string;
  training_id: string;
  resource_file: string;
  id: number;
}
export interface IEvaluationTraineeCreateInput {
  routine_id: number;
  id: number;
  evaluation_phase: number;
  title: string;
  passing_percentage: number;
  is_active: boolean;
  start_date_time: string;
  end_date_time: string;
}

// Evaluation Question
export interface IEvaluationQuestion {
  id: number;
  evaluation_id: number; // references the trainee evaluation
  question_text: string;
  // add other fields like options, type, marks etc. as needed
}

interface EvaluationAttemptFilters {
  page?: number;
  pageSize?: number;
  search?: string;
  evaluationId?: number;
  traineeId?: number;
  isPassed?: boolean;
  routineId?: number;
  isSubmitted?: boolean;
}

export interface IEvaluationQuestionCreateInput {
  evaluation_id: number;
  question_title: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
  correct_option: 'A' | 'B' | 'C' | 'D';
  marks: number;
  display_order: number;
  id: number;
}

// Submit payload (example - adjust based on actual requirements)
export interface IEvaluationSubmitInput {
  evaluation_id: number;
  answers: Array<{
    question_id: number;
    selected_option: string | number | boolean;
  }>;
}

export interface IPrePostComparison {
  pre_score?: number;
  post_score?: number;
  improvement?: number;
}

export interface IRoutineAnalytics {
  // define as per actual response
  total_attempts?: number;
  average_score?: number;
}

export const evaluationTraineeApi = createApi({
  reducerPath: 'evaluationTraineeApi',
  baseQuery: baseQuery,
  tagTypes: [
    'EvaluationTrainee',
    'EvaluationQuestion',
    'EvaluationAttempt',
    'EvaluationQuestions',
    'Resources'
  ],
  endpoints: (build) => ({
    createEvaluationTrainee: build.mutation<
      ApiResponse<IEvaluationTrainee>,
      IEvaluationTraineeCreateInput
    >({
      query: (body) => ({
        url: 'evaluationtrainee/create',
        method: 'POST',
        body
      }),
      invalidatesTags: [{ type: 'EvaluationTrainee', id: 'LIST' }]
    }),

    listResources: build.query<ApiResponse<IResources[]>, number>({
      query: (trainingid: number) => `training/training-resource-list?trainingid=${trainingid}`,
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'Resources' as const, id })),
              { type: 'Resources', id: 'LIST' }
            ]
          : [{ type: 'Resources', id: 'LIST' }]
    }),
    userlistResources: build.query<any, number>({
      query: (routineId: number) => `training/resources-with-training/list?routineId=${routineId}`,

      transformResponse: (response: ApiResponse<any>) => {
        return response.data;
      },

      providesTags: ['Resources']
    }),

    createResources: build.mutation<ApiResponse<IResources>, IResourcesFormInput>({
      query(body: any) {
        const form_data = new FormData();
        for (const key in body) {
          form_data.append(key, body[key] ?? '');
        }
        return {
          url: `training/resource-create`,
          method: 'POST',
          body: form_data
        };
      },
      invalidatesTags: [{ type: 'Resources', id: 'LIST' }]
    }),

    updateResources: build.mutation<
      ApiResponse<IResources>,
      Partial<IResourcesFormInput> & { id: number }
    >({
      query(body: any) {
        const { id } = body;
        const form_data = new FormData();
        for (const key in body) {
          if (key !== 'id') {
            form_data.append(key, body[key] ?? '');
          }
        }

        return {
          url: `training/resource-update/${id}`,
          method: 'PATCH',
          body: form_data
        };
      },
      invalidatesTags: [{ type: 'Resources', id: 'LIST' }]
    }),
    deleteResources: build.mutation<ApiResponse, number>({
      query: (id) => ({
        url: `training/resources/delete/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: [{ type: 'Resources', id: 'LIST' }]
    }),

    updateEvaluationTrainee: build.mutation<
      ApiResponse<IEvaluationTrainee>,
      Partial<IEvaluationTraineeCreateInput>
    >({
      query: ({ ...body }) => ({
        url: `evaluationtrainee/update/${body?.id}`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: [{ type: 'EvaluationTrainee', id: 'LIST' }]
    }),

    listEvaluationTrainee: build.query<ApiResponse<IEvaluationTrainee[]>, number>({
      query: (routineId: number) =>
        `evaluationtrainee/list/?routineId=${routineId}&pageNo=1&pageSize=9999`,
      providesTags: (result, routineId) =>
        result
          ? [
              ...result?.data?.map(({ id }) => ({ type: 'EvaluationTrainee' as const, id })),
              { type: 'EvaluationTrainee', id: 'LIST' },
              { type: 'EvaluationTrainee', id: `ROUTINE-${routineId}` }
            ]
          : [{ type: 'EvaluationTrainee', id: 'LIST' }]
    }),

    getEvaluationTraineeDetail: build.query<ApiResponse<IEvaluationTrainee>, number>({
      query: (id) => `evaluationtrainee/detail/${id}`,
      providesTags: (result, error, id) => [{ type: 'EvaluationTrainee', id }]
    }),

    deleteEvaluationTrainee: build.mutation<ApiResponse, number>({
      query: (id) => ({
        url: `evaluationtrainee/delete/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: [{ type: 'EvaluationTrainee', id: 'LIST' }]
    }),

    // === Evaluation Questions ===
    createEvaluationQuestion: build.mutation<
      ApiResponse<IEvaluationQuestion>,
      IEvaluationQuestionCreateInput
    >({
      query: (body) => ({
        url: 'evaluationtrainee/evaluation-question/create',
        method: 'POST',
        body
      }),
      invalidatesTags: [{ type: 'EvaluationQuestion', id: 'LIST' }]
    }),

    updateEvaluationQuestion: build.mutation<
      ApiResponse<IEvaluationQuestion>,
      Partial<IEvaluationQuestionCreateInput> & { id: number }
    >({
      query: ({ id, ...body }) => ({
        url: `evaluationtrainee/evaluation-question/update/${id}`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: [{ type: 'EvaluationQuestion', id: 'LIST' }]
    }),

    listEvaluationQuestions: build.query<ApiResponse<IEvaluationQuestion[]>, number>({
      query: (evaluationId: number) =>
        `evaluationtrainee/evaluation-questions/list?evaluationId=${evaluationId}`,
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'EvaluationQuestion' as const, id })),
              { type: 'EvaluationQuestion', id: 'LIST' }
            ]
          : [{ type: 'EvaluationQuestion', id: 'LIST' }]
    }),

    listQuestionsByEvaluation: build.query<ApiResponse<IEvaluationQuestion[]>, number>({
      query: (evaluationId) => `evaluationtrainee/list/${evaluationId}`,
      providesTags: (result, error, evaluationId) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'EvaluationQuestion' as const, id })),
              { type: 'EvaluationQuestion', id: `EVAL-${evaluationId}` }
            ]
          : [{ type: 'EvaluationQuestion', id: `EVAL-${evaluationId}` }]
    }),

    getEvaluationQuestionDetail: build.query<ApiResponse<IEvaluationQuestion>, number>({
      query: (id) => `evaluationtrainee/evaluation-question/detail/${id}`,
      providesTags: (result, error, id) => [{ type: 'EvaluationQuestion', id }]
    }),

    deleteEvaluationQuestion: build.mutation<ApiResponse, number>({
      query: (id) => ({
        url: `evaluationtrainee/evaluation-question/delete/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: [{ type: 'EvaluationQuestion', id: 'LIST' }]
    }),

    listEvaluationAttempts: build.query<ApiResponse<any[]>, EvaluationAttemptFilters | void>({
      query: (params) => ({
        url: 'evaluationtrainee/evaluation-attempt/list',
        params: {
          page: params?.page,
          pageSize: params?.pageSize,
          search: params?.search || undefined,
          routineId: params?.routineId,
          evaluationId: params?.evaluationId,
          traineeId: params?.traineeId,
          isPassed: params?.isPassed,
          isSubmitted: params?.isSubmitted
        }
      }),
      providesTags: [{ type: 'EvaluationAttempt', id: 'LIST' }]
    }),

    getEvaluationForm: build.query<ApiResponse<any>, { evaluationPhase: any; routineId: number }>({
      query: ({ evaluationPhase, routineId }) =>
        `evaluationtrainee/evaluation-form/${routineId}/${evaluationPhase}`,
      providesTags: [{ type: 'EvaluationQuestions', id: 'LIST' }]
    }),

    submitEvaluation: build.mutation<ApiResponse<any>, IEvaluationSubmitInput>({
      query: (body) => ({
        url: 'evaluationtrainee/submit',
        method: 'POST',
        body
      }),
      invalidatesTags: [{ type: 'EvaluationQuestions', id: 'LIST' }]
    }),

    getPrePostComparison: build.query<ApiResponse<IPrePostComparison>, number>({
      query: (routineId) => `evaluationtrainee/pre-post-comparison/${routineId}`
    }),

    getPreEvaluationDetails: build.query<ApiResponse<any>, number>({
      query: (routineId) => `evaluationtrainee/pre-evaluation-details/${routineId}`
    }),

    getPostEvaluationDetails: build.query<ApiResponse<any>, number>({
      query: (routineId) => `evaluationtrainee/post-evaluation-details/${routineId}`
    }),

    getRoutineAnalytics: build.query<ApiResponse<IRoutineAnalytics>, number>({
      query: (routineId) => `evaluationtrainee/routine-analytics/${routineId}`
    })
  })
});

export const {
  useCreateEvaluationTraineeMutation,
  useUpdateEvaluationTraineeMutation,
  useListEvaluationTraineeQuery,
  useGetEvaluationTraineeDetailQuery,
  useDeleteEvaluationTraineeMutation,

  useCreateEvaluationQuestionMutation,
  useUpdateEvaluationQuestionMutation,
  useListEvaluationQuestionsQuery,
  useListQuestionsByEvaluationQuery,
  useGetEvaluationQuestionDetailQuery,
  useDeleteEvaluationQuestionMutation,

  useListEvaluationAttemptsQuery,
  useGetEvaluationFormQuery,
  useSubmitEvaluationMutation,
  useGetPrePostComparisonQuery,
  useGetPreEvaluationDetailsQuery,
  useGetPostEvaluationDetailsQuery,
  useGetRoutineAnalyticsQuery,

  // resources APIs
  useListResourcesQuery,
  useCreateResourcesMutation,
  useUpdateResourcesMutation,
  useDeleteResourcesMutation,
  useUserlistResourcesQuery
} = evaluationTraineeApi;
